import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";

/**
 * Landing page for the HubSpot OAuth round-trip.
 *
 * The code/state exchange happens **server-side**: HubSpot redirects straight to the
 * API (`GET /api/hubspot/oauth/callback`), which stores the tokens and then redirects
 * the browser here with `?success=true` (or `?success=false&error=…`). So this page
 * only reports the outcome — it never sees `code`/`state` and must not try to exchange
 * anything itself. Mirrors `PipedriveCallback` / `ZohoCallback` / `DynamicsCallback`.
 */
export const HubspotCallback: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>): string =>
		t(`company.hubspot.callback.${key}`, options as never) as unknown as string;

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const success = searchParams.get("success");
	const error = searchParams.get("error");

	const isSuccess = success === "true";
	const status: "success" | "error" = isSuccess ? "success" : "error";
	const errorMessage = error || ts("errorBody");

	const [countdown, setCountdown] = useState(3);

	useEffect(() => {
		if (status !== "success") {
			return;
		}

		const timer = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					navigate(ROUTE_CONFIG.INTEGRATIONS_HUBSPOT);

					return 0;
				}

				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [status, navigate]);

	return (
		<div className="w-full h-screen flex items-center justify-center bg-gray-50 p-4">
			<div className="flex flex-col bg-white p-8 rounded-[20px] w-[480px] max-w-full shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center">
				{status === "success" && (
					<>
						<Icon className="w-[48px] h-[48px] mb-4 text-status-success" icon={faCircleCheck} />
						<div
							className="text-center text-md font-bold mb-2"
							data-testid="hubspot-callback-success"
						>
							{ts("successTitle")}
						</div>
						<div className="text-center text-ssm text-gray-500 mb-6">
							{ts("successBody", { countdown })}
						</div>
						<div className="flex w-full max-w-[200px] justify-center">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("goToSettings")}
								onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS_HUBSPOT)}
							/>
						</div>
					</>
				)}

				{status === "error" && (
					<>
						<Icon className="w-[48px] h-[48px] mb-4 text-status-error" icon={faCircleXmark} />
						<div
							className="text-center text-md font-bold mb-2"
							data-testid="hubspot-callback-error"
						>
							{ts("errorTitle")}
						</div>
						<div className="text-center text-ssm text-gray-500 mb-6">{errorMessage}</div>
						<div className="flex w-full max-w-[200px] justify-center">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("tryAgain")}
								onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS_HUBSPOT)}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
